import { Children, forwardRef } from 'react'
import { resolveComponentVariant } from '../componentContract'
import styles from './Forms.module.css'
import { InputControl, SelectControl, SwitchControl, TextareaControl } from '../../boardui/Fields'

const sizes = ['compact', 'default']
const resolveSize = (kind, size) => resolveComponentVariant(`${kind} size`, size, sizes, 'default')

// `actions` puts controls inside the input's right edge (a clear X, a show
// password toggle, a colour picker) and keeps the text clear of them. Every
// other prop still lands on the <input>, so Field's id and aria wiring reach it.
// Pass a list, not a fragment (each entry is one action), and pass it (empty
// if need be) for as long as the field can have actions: switching between a
// bare input and one with actions remounts the input and drops focus.
export const Input = forwardRef(function Input({ size = 'default', className = '', actions, ...props }, ref) {
  const input = <InputControl {...props} ref={ref} size={resolveSize('input', size)} className={className} />
  return actions === undefined ? input : <InputActions actions={actions}>{input}</InputActions>
})

// The layout behind Input's `actions`, for an <input> styled outside the
// design system (PasswordInput on the auth pages).
export function InputActions({ actions, children }) {
  const count = Children.toArray(actions).length
  return (
    <div className={styles.inputGroup} style={{ '--wf-input-action-count': count }}>
      {children}
      {count ? <div className={styles.inputActions}>{actions}</div> : null}
    </div>
  )
}

export const Textarea = forwardRef(function Textarea({ size = 'default', className = '', ...props }, ref) {
  return <TextareaControl {...props} ref={ref} size={resolveSize('textarea', size)} className={className} />
})

export const Select = forwardRef(function Select({ size = 'default', className = '', children, ...props }, ref) {
  return <SelectControl {...props} ref={ref} size={resolveSize('select', size)} className={className}>{children}</SelectControl>
})

function Choice({ type, label, description, className = '', disabled = false, ...props }) {
  return (
    <label className={`${styles.choice} ${disabled ? styles.choiceDisabled : ''} ${className}`.trim()}>
      <input {...props} type={type} disabled={disabled} />
      <span className={styles.choiceText}>
        <span className={styles.choiceLabel}>{label}</span>
        {description ? <span className={styles.choiceDescription}>{description}</span> : null}
      </span>
    </label>
  )
}

export function Checkbox(props) { return <Choice {...props} type="checkbox" /> }
export function Radio(props) { return <Choice {...props} type="radio" /> }

// The compatibility adapter keeps onChange(boolean, event), native form state,
// and the Toggle checkbox role while BoardUI supplies the track presentation.
export const Switch = SwitchControl
